import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ onFiltersChange, isCollapsed, onToggleCollapse }) => {
  const [filters, setFilters] = useState({
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
    riskLevel: '',
    transactionType: 'all',
    confirmationStatus: 'all',
    flags: []
  });

  const transactionTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'normal', label: 'Normal' },
    { value: 'suspicious', label: 'Suspicious' },
    { value: 'mixer', label: 'Mixer Service' },
    { value: 'exchange', label: 'Exchange' },
    { value: 'split', label: 'Fund Splitting' }
  ];

  const confirmationStatuses = [
    { value: 'all', label: 'All Status' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  const availableFlags = [
    'suspicious_amount',
    'new_address',
    'exchange_deposit',
    'mixer_service',
    'high_risk',
    'fund_splitting',
    'rapid_movement',
    'large_transaction'
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange && onFiltersChange(newFilters);
  };

  const handleFlagToggle = (flag) => {
    const newFlags = filters?.flags?.includes(flag)
      ? filters?.flags?.filter(f => f !== flag)
      : [...filters?.flags, flag];
    
    handleFilterChange('flags', newFlags);
  };

  const handleReset = () => {
    const resetFilters = {
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
      riskLevel: '',
      transactionType: 'all',
      confirmationStatus: 'all',
      flags: []
    };
    setFilters(resetFilters);
    onFiltersChange && onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.minAmount) count++;
    if (filters?.maxAmount) count++;
    if (filters?.dateFrom) count++;
    if (filters?.dateTo) count++;
    if (filters?.riskLevel) count++;
    if (filters?.transactionType !== 'all') count++;
    if (filters?.confirmationStatus !== 'all') count++;
    if (filters?.flags?.length > 0) count++;
    return count;
  };

  if (isCollapsed) {
    return (
      <div className="bg-surface border border-border rounded-lg shadow-forensic-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-text-primary">Filters</h3>
            {getActiveFilterCount() > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
          >
            <Icon name="ChevronDown" size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg shadow-forensic-card">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Advanced Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={getActiveFilterCount() === 0}
          >
            <Icon name="RotateCcw" size={16} className="mr-1" />
            Reset
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
          >
            <Icon name="ChevronUp" size={16} />
          </Button>
        </div>
      </div>
      {/* Filter Content */}
      <div className="p-4 space-y-6">
        {/* Amount Range */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Amount Range (BTC)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              type="number"
              label="Minimum Amount"
              placeholder="0.00000000"
              value={filters?.minAmount}
              onChange={(e) => handleFilterChange('minAmount', e?.target?.value)}
              step="0.00000001"
            />
            <Input
              type="number"
              label="Maximum Amount"
              placeholder="999.99999999"
              value={filters?.maxAmount}
              onChange={(e) => handleFilterChange('maxAmount', e?.target?.value)}
              step="0.00000001"
            />
          </div>
        </div>

        {/* Date Range */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Date Range</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              type="date"
              label="From Date"
              value={filters?.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
            />
            <Input
              type="date"
              label="To Date"
              value={filters?.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
            />
          </div>
        </div>

        {/* Risk Level */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Minimum Risk Score</h4>
          <div className="space-y-2">
            <Input
              type="range"
              min="0"
              max="100"
              value={filters?.riskLevel || 0}
              onChange={(e) => handleFilterChange('riskLevel', e?.target?.value)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-text-secondary">
              <span>Low Risk (0)</span>
              <span className="font-medium text-text-primary">
                {filters?.riskLevel || 0}
              </span>
              <span>High Risk (100)</span>
            </div>
          </div>
        </div>

        {/* Transaction Type */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Transaction Type</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {transactionTypes?.map((type) => (
              <button
                key={type?.value}
                onClick={() => handleFilterChange('transactionType', type?.value)}
                className={`
                  p-2 text-sm rounded-lg border transition-forensic
                  ${filters?.transactionType === type?.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-surface text-text-secondary border-border hover:border-primary hover:text-text-primary'
                  }
                `}
              >
                {type?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Confirmation Status */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Confirmation Status</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {confirmationStatuses?.map((status) => (
              <button
                key={status?.value}
                onClick={() => handleFilterChange('confirmationStatus', status?.value)}
                className={`
                  p-2 text-sm rounded-lg border transition-forensic
                  ${filters?.confirmationStatus === status?.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-surface text-text-secondary border-border hover:border-primary hover:text-text-primary'
                  }
                `}
              >
                {status?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Transaction Flags */}
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-3">Transaction Flags</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {availableFlags?.map((flag) => (
              <button
                key={flag}
                onClick={() => handleFlagToggle(flag)}
                className={`
                  p-2 text-sm rounded-lg border transition-forensic text-left
                  ${filters?.flags?.includes(flag)
                    ? 'bg-accent text-accent-foreground border-accent'
                    : 'bg-surface text-text-secondary border-border hover:border-accent hover:text-text-primary'
                  }
                `}
              >
                <div className="flex items-center space-x-2">
                  <div className={`
                    w-3 h-3 rounded border-2 flex items-center justify-center
                    ${filters?.flags?.includes(flag) ? 'border-accent-foreground bg-accent-foreground' : 'border-border'}
                  `}>
                    {filters?.flags?.includes(flag) && (
                      <Icon name="Check" size={8} color="var(--color-accent)" />
                    )}
                  </div>
                  <span>{flag?.replace(/_/g, ' ')}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Apply Filters Button */}
      <div className="p-4 border-t border-border bg-muted">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
          </div>
          <Button
            variant="default"
            onClick={() => onFiltersChange && onFiltersChange(filters)}
          >
            <Icon name="Search" size={16} className="mr-2" />
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;